import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Card,
  CardBody,
  HStack,
  VStack,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { IoRefreshOutline, IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Loader from "../components/loader";
import useHealth from "../hooks/useHealth";
import { getServerHealth, getRecoveries } from "../store/actions/healthActions";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Africa/Windhoek");

const ServerHealth = () => {
  const dispatch = useDispatch();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const {
    healthLoading,
    healthError,
    hasData,
    isHealthy,
    databaseConnected,
    smppState,
    smppIsHealthy,
    smppMessagesSent,
    smppMessagesDelivered,
    smppDeliveryRate,
    uptimeFormatted,
    operations24h,
    operations1h,
    rabbitmqQueueLength24h,
    smppBound24h,
    smppConnected24h,
    smppDisconnected24h,
    smppUnbound24h,
    rabbitmqQueueLength1h,
    smppBound1h,
    smppConnected1h,
    smppDisconnected1h,
    smppUnbound1h,
    lastUpdated,
    recoveries,
    recoveriesCount,
  } = useHealth();

  // Fetch health data and recoveries on mount
  useEffect(() => {
    dispatch(getServerHealth());
    dispatch(getRecoveries());
  }, [dispatch]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      dispatch(getServerHealth());
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, dispatch]);

  const handleManualRefresh = () => {
    dispatch(getServerHealth());
  };

  const getSmppStateBadge = () => {
    const stateColors = {
      BOUND: "green",
      CONNECTED: "blue",
      CONNECTING: "yellow",
      DISCONNECTED: "red",
      ERROR: "red",
      CLOSING: "orange",
      UNKNOWN: "gray",
    };

    return (
      <Badge colorScheme={stateColors[smppState] || "gray"} fontSize="md" px={3} py={1}>
        {smppState}
      </Badge>
    );
  };

  const getStatusIcon = (isOk) => {
    return isOk ? (
      <IoCheckmarkCircle size={24} color="green" />
    ) : (
      <IoCloseCircle size={24} color="red" />
    );
  };

  if (healthLoading && !hasData) {
    return <Loader />;
  }

  return (
    <Box>
      <Helmet>
        <title>Server Health</title>
      </Helmet>

      {/* Header */}
      <HStack justifyContent="space-between" mb={4}>
        <Box>
          <Heading fontSize="34px" color="#222" fontWeight="700">
            Server Health Monitor
          </Heading>
          {lastUpdated && (
            <Text fontSize="sm" color="gray.600">
              Last updated: {dayjs(lastUpdated).format("MMM D, YYYY h:mm:ss A")}
            </Text>
          )}
        </Box>
        <HStack>
          <Button
            size="sm"
            variant={autoRefresh ? "solid" : "outline"}
            colorScheme={autoRefresh ? "blue" : "gray"}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto-refresh: {autoRefresh ? "ON" : "OFF"}
          </Button>
          <Button
            leftIcon={<IoRefreshOutline />}
            onClick={handleManualRefresh}
            isLoading={healthLoading}
            size="sm"
            colorScheme="blue"
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      {/* Error Alert */}
      {healthError && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertTitle>Error fetching health data</AlertTitle>
          <AlertDescription>{healthError}</AlertDescription>
        </Alert>
      )}

      {/* Overall Status Alert */}
      {hasData && (
        <Alert
          status={isHealthy ? "success" : "error"}
          mb={4}
          borderRadius="md"
        >
          <AlertIcon />
          <AlertTitle>
            System Status: {isHealthy ? "Healthy" : "Unhealthy"}
          </AlertTitle>
        </Alert>
      )}

      {/* Service Status Cards */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
        {/* SMPP Service Status */}
        <Card>
          <CardBody>
            <HStack justifyContent="space-between" mb={3}>
              <Heading size="md">SMPP Service</Heading>
              {getStatusIcon(smppIsHealthy)}
            </HStack>
            <VStack align="stretch" spacing={3}>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Connection State:</Text>
                {getSmppStateBadge()}
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Uptime:</Text>
                <Text>{uptimeFormatted}</Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Messages Sent:</Text>
                <Text>{smppMessagesSent.toLocaleString()}</Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Messages Delivered:</Text>
                <Text>{smppMessagesDelivered.toLocaleString()}</Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Delivery Rate:</Text>
                <Text>{smppDeliveryRate}</Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Database Status */}
        <Card>
          <CardBody>
            <HStack justifyContent="space-between" mb={3}>
              <Heading size="md">Database</Heading>
              {getStatusIcon(databaseConnected)}
            </HStack>
            <VStack align="stretch" spacing={3}>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Connection Status:</Text>
                <Badge colorScheme={databaseConnected ? "green" : "red"} fontSize="md">
                  {databaseConnected ? "Connected" : "Disconnected"}
                </Badge>
              </HStack>
              <Text fontSize="sm" color="gray.600">
                RethinkDB connection is {databaseConnected ? "active and operational" : "not available"}
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* 24-Hour Metrics */}
      <Box mb={6}>
        <Heading size="md" mb={4}>
          Last 24 Hours ({operations24h.toLocaleString()} operations)
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>RabbitMQ Queue</StatLabel>
                <StatNumber>{rabbitmqQueueLength24h.toLocaleString()}</StatNumber>
                <StatHelpText>Queue depth events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Bound</StatLabel>
                <StatNumber color="green.600">{smppBound24h.toLocaleString()}</StatNumber>
                <StatHelpText>Bind events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Connected</StatLabel>
                <StatNumber color="blue.600">{smppConnected24h.toLocaleString()}</StatNumber>
                <StatHelpText>Connect events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Disconnected</StatLabel>
                <StatNumber color="orange.600">{smppDisconnected24h.toLocaleString()}</StatNumber>
                <StatHelpText>Disconnect events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Unbound</StatLabel>
                <StatNumber color="red.600">{smppUnbound24h.toLocaleString()}</StatNumber>
                <StatHelpText>Unbind events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      </Box>

      <Divider mb={6} />

      {/* 1-Hour Metrics */}
      <Box>
        <Heading size="md" mb={4}>
          Last Hour ({operations1h.toLocaleString()} operations)
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>RabbitMQ Queue</StatLabel>
                <StatNumber>{rabbitmqQueueLength1h.toLocaleString()}</StatNumber>
                <StatHelpText>Queue depth events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Bound</StatLabel>
                <StatNumber color="green.600">{smppBound1h.toLocaleString()}</StatNumber>
                <StatHelpText>Bind events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Connected</StatLabel>
                <StatNumber color="blue.600">{smppConnected1h.toLocaleString()}</StatNumber>
                <StatHelpText>Connect events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Disconnected</StatLabel>
                <StatNumber color="orange.600">{smppDisconnected1h.toLocaleString()}</StatNumber>
                <StatHelpText>Disconnect events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>SMPP Unbound</StatLabel>
                <StatNumber color="red.600">{smppUnbound1h.toLocaleString()}</StatNumber>
                <StatHelpText>Unbind events</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      </Box>

      {/* Recovery History */}
      {recoveriesCount > 0 && (
        <>
          <Divider mb={6} />
          <Box>
            <Heading size="md" mb={4}>
              Recovery History ({recoveriesCount} events in last 24 hours)
            </Heading>
            <Card>
              <CardBody>
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Time</Th>
                        <Th>Type</Th>
                        <Th>Reason</Th>
                        <Th>Previous State</Th>
                        <Th>New State</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {recoveries.map((recovery, index) => (
                        <Tr key={recovery.id || index}>
                          <Td>{dayjs.unix(recovery.timestamp).tz("Africa/Windhoek").format("MMM D, YYYY h:mm:ss A")}</Td>
                          <Td>
                            <Badge
                              colorScheme={
                                recovery.recovery_type === "auto" ? "blue" :
                                recovery.recovery_type === "manual_api" ? "purple" :
                                recovery.recovery_type === "manual_pm2" ? "orange" : "gray"
                              }
                            >
                              {recovery.recovery_type === "auto" ? "Auto" :
                               recovery.recovery_type === "manual_api" ? "Manual (API)" :
                               recovery.recovery_type === "manual_pm2" ? "Manual (PM2)" : recovery.recovery_type}
                            </Badge>
                          </Td>
                          <Td>{recovery.reason || "N/A"}</Td>
                          <Td>
                            <Badge colorScheme="blue" variant="outline">
                              {recovery.metadata?.previousState || recovery.metadata?.state || "N/A"}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme="green" variant="outline">
                              {recovery.metadata?.newState || (recovery.metadata?.consecutiveTimeouts ? "Reconnecting" : "N/A")}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ServerHealth;
